import type AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { type GrantActor, grantedOnBy, removedOn } from "permissions/_views/wording/permission-wording";
import { personActor, removerOf } from "permissions/_views/grant-row/grant-row-model";
import { type PersonNames, personNameState, removerNameState } from "permissions/_views/targets/person-name-states";
import type Permission from "permissions/permission";

/**
 * What the details pane says of each grant the chosen one replaced (#6825): when it was granted and by
 * whom, and when it was removed and by whom. Kept apart from the component so the wording is a plain
 * function a test can read without rendering.
 */
export type GrantHistoryEntry = Readonly<{ id: string; granted: string; removed: string }>;

function grantorOf(grant: Permission, viewerUserId: Guid, names: Pick<PersonNames, "states">): GrantActor | undefined {
  if (grant.grantorUserId.isEqualTo(Guid.EMPTY)) return { type: "system" };
  return personActor(grant.grantorUserId, viewerUserId, personNameState(names, grant.grantorUserId.value));
}

/** The replaced grants, in the order the server gave them: newest first. */
export function grantHistoryEntries(
  replaced: readonly Permission[],
  viewerUserId: Guid,
  names: Pick<PersonNames, "states">,
  today: AHDate,
): GrantHistoryEntry[] {
  return replaced.map((grant, index) => ({
    id: grant.id?.value ?? `replaced-${index}`,
    granted: grantedOnBy(grant.createdDate, today, grantorOf(grant, viewerUserId, names)),
    removed: removedOn(grant.disabledDate, today, removerOf(grant, viewerUserId, removerNameState(names, grant))),
  }));
}
